import { createRng, hashSeed } from './rng.js';
import { letterFor } from './grading.js';

/**
 * What happens to the rooms you did not set.
 *
 * The board posts twice as much work as a shift holds, which is only a decision
 * if the events you walk away from actually happen. Somebody else sets them, or
 * half-sets them, and the department whose evening it was finds out how it
 * went. Time does not wait for the player and rooms do not sit frozen.
 *
 * The model is three numbers and a coin:
 *
 *   baseline   your own rolling average. The crew mirrors you — a banquets
 *              department run by somebody who sets an A room is a department
 *              where the B team is better than it would otherwise be. This is
 *              the part that makes your reputation worth something beyond the
 *              events you personally touch.
 *   penalty    a flat deduction, because it is always worse without you.
 *   stretch    scaled by how much of the board went unset, beyond the share
 *              the board always leaves over (`EXPECTED_UNSET`). Walking away
 *              from one event is a colleague covering; walking away from all
 *              of them is a department that is underwater, and the difference
 *              has to show.
 *
 * Plus noise, so two identical nights are not identical. The noise is seeded on
 * the event and the day, which means it survives a reload — a ghost that
 * re-rolls its grade when you reopen the tab is a save the player cannot trust.
 */

/** Where a ghost starts before you have set anything. A weak C. */
export const DEFAULT_BASELINE = 0.68;

/** Flat cost of not being there. */
export const ABSENCE_PENALTY = 0.12;

/** Extra cost per unit of board left unset, at full load. */
export const STRETCH_PENALTY = 0.3;

/**
 * How much of a board goes unset with nobody stretched by it.
 *
 * The board posts 1.6 to 2 times what a shift holds (`OVERSUBSCRIBE`), so even
 * a skilled player leaves half of it to the B team, and a new one leaves three
 * quarters. Counting all of that as a department underwater graded nearly every
 * ghost a D for a new player, and in the second act, where the act's own
 * departments post four times as many rooms (`FAVOURED`), their ghosts took
 * more from the Kitchen than the rooms the player set gave it (`fullgame.mjs`,
 * days five to eight). Only what is left beyond this share stretches anybody.
 */
export const EXPECTED_UNSET = 0.6;

export const GHOST_VARIANCE = 0.09;

/** How far a ghost drags Banquets down when it goes badly. */
export const BANQUETS_SHARE = 0.3;

/**
 * What a room you were not there for, or one the doors caught unfinished,
 * costs in the first act, as a share of what it costs after.
 *
 * The first act is a new starter's: they set a room a shift, slowly, and the
 * rest of a board twice the size of the shift goes ahead without them. At full
 * price one bad day sank every department in it before the story had started
 * (`fullgame.mjs`). The good side is untouched; only losses are eased.
 */
export const FIRST_ACT_EASE = 0.4;

export function createGhosts({ standing, seed = 'lion' }) {
  /** The player's own grades, most recent last. Only the last few matter. */
  let scores = [];
  let resolved = [];

  function baseline() {
    if (!scores.length) return DEFAULT_BASELINE;
    const recent = scores.slice(-5);
    return recent.reduce((total, score) => total + score, 0) / recent.length;
  }

  return {
    get baseline() {
      return baseline();
    },
    get resolved() {
      return resolved;
    },
    forDay: (day) => resolved.filter((entry) => entry.day === day),

    /** Records one of the player's own grades. Ghosts track it. */
    record(score) {
      scores = [...scores, score].slice(-10);
    },

    /**
     * Resolves one event nobody set.
     *
     * `load` is 0..1: how much of the day's board is going unset. The caller
     * knows the board; this only knows how to be disappointed by it.
     */
    resolve({ beo, day, load = 0, ease = 1 }) {
      const rng = createRng(hashSeed(seed, 'ghost', beo.id, day));
      const score = clamp01(
        baseline() -
          ABSENCE_PENALTY -
          STRETCH_PENALTY * clamp01((load - EXPECTED_UNSET) / (1 - EXPECTED_UNSET)) +
          rng.gauss() * GHOST_VARIANCE
      );
      const grade = letterFor(score);

      /*
       * Direct, never through the rivalry matrix.
       *
       * An event burning is not you winning. Run through the matrix, the
       * Kitchen's ruined evening would pay out to Banquets — the game
       * congratulating you on somebody else's disaster.
       */
      const owner = beo.owner ?? 'banquets';
      const award = beo.award?.[owner] ?? beo.award?.banquets ?? 10;
      // `ease` scales a loss and never a gain (`FIRST_ACT_EASE`).
      const eased = grade.multiplier < 0 ? grade.multiplier * ease : grade.multiplier;
      const moves = { [owner]: Math.round(award * eased * 10) / 10 };

      // The ballroom is Banquets' room. A shambles in it reflects on you even
      // when you were somewhere else, but a competent job by the B team earns
      // you nothing — you were not there.
      if (grade.multiplier < 0 && owner !== 'banquets') {
        moves.banquets = Math.round(award * eased * BANQUETS_SHARE * 10) / 10;
      }

      const event = standing.applyDirect(moves, {
        reason: `${beo.code} went ahead without you, graded ${grade.letter}`,
      });

      const entry = {
        id: beo.id,
        code: beo.code,
        title: beo.title,
        owner,
        day,
        score,
        letter: grade.letter,
        changes: event.changes,
      };
      resolved = [...resolved, entry].slice(-60);
      return entry;
    },

    clear() {
      scores = [];
      resolved = [];
    },

    toJSON: () => ({ scores, resolved }),
    load(state) {
      scores = [...(state?.scores ?? [])];
      resolved = [...(state?.resolved ?? [])];
    },
  };
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}
